import i18n from '@/i18n';
import type { Place } from '@/types';
import { getCategoryColor, getCategoryLabel, getPrimaryColor, getSecondaryColor } from '@/utils/colors';
import { Loader } from '@googlemaps/js-api-loader';

const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;
if (!MAP_ID) { throw new Error("Missing Google Maps Map ID") }

export class MapManager {
  private map: google.maps.Map | null = null;
  private markers: Map<string, google.maps.marker.AdvancedMarkerElement[]> = new Map();
  private infoWindow: google.maps.InfoWindow | null = null;
  private loader: Loader;
  private activeMarkerId: string | null = null;

  constructor(apiKey: string) {
    this.loader = new Loader({
      apiKey,
      version: 'weekly',
      libraries: ['marker'],
    });
  }

  /**
   * Initialize the Google Map
   */
  async initMap(element: HTMLElement, center?: google.maps.LatLngLiteral): Promise<void> {
    try {
      await this.loader.importLibrary("core");
      // Ensure the marker library (AdvancedMarkerElement/PinElement) is available
      await this.loader.importLibrary("marker");

      const defaultCenter = center || { lat: 50.0755, lng: 14.4378 }; // Prague as default

      this.map = new google.maps.Map(element, {
        center: defaultCenter,
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        mapId: MAP_ID, // Required for Advanced Markers
      });

      this.infoWindow = new google.maps.InfoWindow();
    } catch (error) {
      console.error('Failed to initialize Google Maps:', error);
      throw error;
    }
  }

  /**
   * Add markers for places
   */
  addMarkers(places: Place[], onMarkerClick?: (place: Place) => void): void {
    if (!this.map) {
      console.error('Map not initialized');
      return;
    }

    // Clear existing markers
    this.clearMarkers();

    places.forEach((place) => {
      const primaryColor = getPrimaryColor(place.categories);
      const secondaryColor = getSecondaryColor(place.categories);

      const placeMarkers = place.coordinates.map((coordinate) => {
        const markerElement = this.createLogoMarkerElement(
          place.brandLogo,
          primaryColor,
          secondaryColor,
          place.title
        );

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          position: {
            lat: coordinate.lat,
            lng: coordinate.lon,
          },
          title: place.title,
          content: markerElement,
        });

        marker.addListener('gmp-click', () => {
          this.showInfoWindow(place, marker);
          if (onMarkerClick) {
            onMarkerClick(place);
          }
        });

        return marker;
      });

      if (placeMarkers.length > 0) {
        this.markers.set(place.id, placeMarkers);
      }
    });

    // Fit map bounds to show all markers
    if (places.length > 0) {
      this.fitBounds(places);
    }
  }

  /**
   * Show info window for a place
   */
  showInfoWindow(place: Place, marker: google.maps.Marker | google.maps.marker.AdvancedMarkerElement): void {
    if (!this.infoWindow || !this.map) return;

    const content = this.createInfoWindowContent(place);
    this.infoWindow.setContent(content);
    // InfoWindow can anchor to both classic and advanced markers
    this.infoWindow.open(this.map, marker as unknown as google.maps.MVCObject);
    this.activeMarkerId = place.id;
  }

  /**
   * Create HTML content for info window
   */
  private createInfoWindowContent(place: Place): string {
    const categoryIndicators = place.categories
      .slice(0, 2)
      .map(
        (cat) =>
          `<span class="indicator" style="background-color: ${getCategoryColor(cat)};"></span>`
      )
      .join('');

    const logoHTML = place.brandLogo
      ? `<img src="${place.brandLogo}" alt="${place.title}" class="info-logo" />`
      : '';

    const websiteHTML = place.websiteURL
      ? `<a href="${place.websiteURL}" target="_blank" rel="noopener noreferrer" class="info-link">${i18n.t('map.visitWebsite')}</a>`
      : '';

    const customContentHTML = place.customHTML
      ? `<div class="custom-content">${place.customHTML}</div>`
      : '';

    const categoryTags = place.categories
      .map((cat) => `<span>${getCategoryLabel(cat)}</span>`)
      .join(', ');

    return `
      <div class="info-window">
        <div class="info-header">
          <h3>${place.title}</h3>
          <div class="category-indicators">
            ${categoryIndicators}
          </div>
        </div>
        ${logoHTML}
        <p class="info-description">${place.description}</p>
        <p style="font-size: 12px; color: #7f8c8d; margin-top: 8px;">
          <strong>${i18n.t('map.categories')}</strong> ${categoryTags}
        </p>
        ${websiteHTML}
        ${customContentHTML}
      </div>
    `;
  }

  /**
   * Center map on a specific place and show its info window
   */
  focusOnPlace(place: Place): void {
    if (!this.map) return;

    const placeMarkers = this.markers.get(place.id);
    if (!placeMarkers || placeMarkers.length === 0) return;

    const [firstCoordinate] = place.coordinates;
    if (!firstCoordinate) return;

    this.map.setCenter({
      lat: firstCoordinate.lat,
      lng: firstCoordinate.lon,
    });
    this.map.setZoom(15);

    this.showInfoWindow(place, placeMarkers[0]);
  }

  /**
   * Filter visible markers based on place IDs
   */
  filterMarkers(visiblePlaceIds: Set<string>): void {
    this.markers.forEach((placeMarkers, placeId) => {
      const shouldShow = visiblePlaceIds.has(placeId);

      placeMarkers.forEach((marker) => {
        marker.map = shouldShow ? this.map : null;
      });
    });
  }

  /**
   * Clear all markers from the map
   */
  clearMarkers(): void {
    this.markers.forEach((placeMarkers) => {
      placeMarkers.forEach((marker) => {
        marker.map = null;
      });
    });
    this.markers.clear();
    this.activeMarkerId = null;
  }

  /**
   * Fit map bounds to show all places
   */
  private fitBounds(places: Place[]): void {
    if (!this.map || places.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    places.forEach((place) => {
      place.coordinates.forEach((coordinate) => {
        bounds.extend({
          lat: coordinate.lat,
          lng: coordinate.lon,
        });
      });
    });

    this.map.fitBounds(bounds);

    // Ensure minimum zoom level
    const listener = google.maps.event.addListener(this.map, 'idle', () => {
      const currentZoom = this.map?.getZoom();
      if (this.map && currentZoom && currentZoom > 16) {
        this.map.setZoom(16);
      }
      google.maps.event.removeListener(listener);
    });
  }

  /**
   * Get active marker ID
   */
  getActiveMarkerId(): string | null {
    return this.activeMarkerId;
  }

  /**
   * Close info window
   */
  closeInfoWindow(): void {
    if (this.infoWindow) {
      this.infoWindow.close();
      this.activeMarkerId = null;
    }
  }

  /**
   * Create a PinElement for the AdvancedMarker with optional brand logo glyph.
   * - Uses primary color for pin background
   * - Uses secondary color for border when present
   * - If brandLogo is present, it is displayed inside the pin using glyph property with URL
   * - If no logo, shows a small dual-color circular glyph to preserve two-tone branding
   */
  private createLogoMarkerElement(
    logoUrl: string | null,
    primaryColor: string,
    secondaryColor: string | null,
    title: string
  ): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'map-logo-marker';
    wrapper.style.width = '64px';
    wrapper.style.height = '64px';
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    wrapper.style.borderRadius = '50%';
    wrapper.style.padding = '4px';
    wrapper.style.boxSizing = 'border-box';
    wrapper.style.boxShadow = '0 2px 8px rgba(0,0,0,0.35)';
    wrapper.style.background = secondaryColor
      ? `conic-gradient(${primaryColor} 0deg 180deg, ${secondaryColor} 180deg 360deg)`
      : primaryColor;

    const inner = document.createElement('div');
    inner.style.width = '100%';
    inner.style.height = '100%';
    inner.style.borderRadius = '50%';
    inner.style.background = '#ffffff';
    inner.style.overflow = 'hidden';
    inner.style.display = 'flex';
    inner.style.alignItems = 'center';
    inner.style.justifyContent = 'center';
    inner.style.boxSizing = 'border-box';
    inner.title = title;

    if (logoUrl) {
      const img = document.createElement('img');
      img.src = logoUrl;
      img.alt = title;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';
      inner.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      placeholder.textContent = title.charAt(0);
      placeholder.style.fontSize = '24px';
      placeholder.style.fontWeight = '600';
      placeholder.style.color = '#34495e';
      inner.appendChild(placeholder);
    }

    wrapper.appendChild(inner);

    return wrapper;
  }
}
